// Import primary languages statically for fast initial render (no fetch needed)
// These cover English, Spanish-speaking, and Arabic-speaking regions
import englishMessages from '@/../../shared/translations/english.json';
import spanishMessages from '@/../../shared/translations/spanish.json';
import arabicMessages from '@/../../shared/translations/arabic.json';

export const LocaleToLanguage: { [key: string]: string } = {
  "af": 'afrikaans',
  "ar": 'arabic',
  "hy": 'armenian',
  "az": 'azerbaijani',
  "be": 'belarusian',
  "bs": 'bosnian',
  "bg": 'bulgarian',
  "ca": 'catalan',
  "yue": 'cantonese',
  "zh": 'mandarin_chinese',
  "hr": 'croatian',
  "cs": 'czech',
  "da": 'danish',
  "nl": 'dutch',
  "en": 'english',
  "et": 'estonian',
  "fi": 'finnish',
  "fr": 'french',
  "gl": 'galician',
  "de": 'german',
  "el": 'greek',
  "gu": 'gujarati',
  "he": 'hebrew',
  "hi": 'hindi',
  "hu": 'hungarian',
  "is": 'icelandic',
  "id": 'indonesian',
  "in": 'indonesian',
  "it": 'italian',
  "ja": 'japanese',
  "kn": 'kannada',
  "kk": 'kazakh',
  "ko": 'korean',
  "lv": 'latvian',
  "lt": 'lithuanian',
  "mk": 'macedonian',
  "ms": 'malay',
  "mr": 'marathi',
  "mi": 'maori',
  "ne": 'nepali',
  "no": 'norwegian',
  "fa": 'persian',
  "pl": 'polish',
  "pt": 'portuguese',
  "ro": 'romanian',
  "ru": 'russian',
  "sr": 'serbian',
  "sk": 'slovak',
  "sl": 'slovenian',
  "es": 'spanish',
  "sw": 'swahili',
  "sv": 'swedish',
  "tl": 'tagalog',
  "ta": 'tamil',
  "th": 'thai',
  "tr": 'turkish',
  "uk": 'ukrainian',
  "ur": 'urdu',
  "vi": 'vietnamese',
  "cy": 'welsh',
};

// Cache for loaded language translations (pre-populated with bundled languages)
const languageCache: { [key: string]: { [key: string]: string } } = {
  english: englishMessages as { [key: string]: string },
  spanish: spanishMessages as { [key: string]: string },
  arabic: arabicMessages as { [key: string]: string },
};

// Track in-flight fetch requests to avoid duplicate requests
const fetchPromises: { [key: string]: Promise<{ [key: string]: string }> } = {};

/**
 * Fetch translations for a language from the server.
 * Returns cached version if available.
 */
async function fetchLanguageTranslations(language: string): Promise<{ [key: string]: string }> {
  const normalizedLanguage = (language || '').trim().toLowerCase();
  if (!normalizedLanguage) {
    return languageCache.english;
  }

  // Return cached version if available
  if (languageCache[normalizedLanguage]) {
    return languageCache[normalizedLanguage];
  }

  // Return existing fetch promise if in-flight
  const existingPromise = fetchPromises[normalizedLanguage];
  if (existingPromise !== undefined) {
    return existingPromise;
  }

  // Start new fetch
  fetchPromises[normalizedLanguage] = (async () => {
    try {
      const response = await fetch(`/translations/${normalizedLanguage}.json`);
      if (!response.ok) {
        console.warn(`Failed to load translations for ${normalizedLanguage}, falling back to English`);
        return languageCache.english;
      }
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        console.warn(`Invalid translation response for ${normalizedLanguage}, falling back to English`);
        return languageCache.english;
      }
      const translations = await response.json();
      languageCache[normalizedLanguage] = translations;
      return translations;
    } catch (error) {
      console.warn(`Error loading translations for ${normalizedLanguage}:`, error);
      return languageCache.english;
    } finally {
      delete fetchPromises[normalizedLanguage];
    }
  })();

  return fetchPromises[normalizedLanguage];
}

/**
 * Get messages for a language synchronously.
 * Returns English if the language is not loaded yet.
 * Use loadLanguageAsync() first for non-English languages.
 */
export function getMessagesFromLanguage(language: string): { [key: string]: string } {
  const normalizedLanguage = (language || '').trim().toLowerCase();
  // Return cached version if available
  if (languageCache[normalizedLanguage]) {
    // Merge with English as fallback for any missing keys
    return { ...languageCache.english, ...languageCache[normalizedLanguage] };
  }

  // Not cached yet - return English as fallback
  // The async version should be called first
  return languageCache.english;
}

/**
 * Load translations for a language asynchronously.
 * Call this when user changes their GUI language.
 */
export async function loadLanguageAsync(language: string): Promise<{ [key: string]: string }> {
  const normalizedLanguage = (language || '').trim().toLowerCase();
  if (!normalizedLanguage || normalizedLanguage === 'english') {
    return languageCache.english;
  }

  const translations = await fetchLanguageTranslations(normalizedLanguage);
  // Merge with English as fallback for any missing keys
  return { ...languageCache.english, ...translations };
}

/**
 * Get messages from locale code (e.g., 'en', 'es', 'fr').
 */
export function getMessagesFromLocale(locale: string): { [key: string]: string } {
  return getMessagesFromLanguage(LocaleToLanguage[locale] || 'english');
}

/**
 * Load messages from locale code asynchronously.
 */
export async function loadMessagesFromLocaleAsync(locale: string): Promise<{ [key: string]: string }> {
  return loadLanguageAsync(LocaleToLanguage[locale] || 'english');
}

/**
 * Pre-load translations for common languages.
 * Call this at app startup for better UX.
 */
export async function preloadCommonLanguages(): Promise<void> {
  const commonLanguages = ['spanish', 'portuguese', 'french', 'german'];
  await Promise.all(commonLanguages.map(lang => fetchLanguageTranslations(lang)));
}

/**
 * Check if a language is already loaded in cache.
 */
export function isLanguageLoaded(language: string): boolean {
  return language in languageCache;
}
